/**
 * @fileoverview Legal-page chrome strings barrel — the EN/ES bundles plus
 *   `loadLegalContentStrings(i18nInstance, locales)`, which registers them under the `legalContent`
 *   namespace on a react-i18next instance.
 * @package @puffin-core/legal-content
 *
 * ## 🔴 LEAF-ONLY by design — this subpath must not import a component or a document
 *
 * `@puffin-core/i18n-namespaces` imports this module at APP BOOT from every web app AND every marketing
 * portal, so anything reachable from here lands in every boot chunk. It imports only its own frozen data
 * modules, a zero-import namespace constant, and the React-free strings-kit resolver — never the document
 * registry.
 *
 * @module @puffin-core/legal-content/strings
 */

import { LEGAL_CONTENT_EN } from './en.js';
import { LEGAL_CONTENT_ES } from './es.js';
import { LEGAL_CONTENT_NAMESPACE } from './namespace.js';

export { LEGAL_CONTENT_EN, LEGAL_CONTENT_ES, LEGAL_CONTENT_NAMESPACE };

const BUNDLES = Object.freeze({ en: LEGAL_CONTENT_EN, es: LEGAL_CONTENT_ES });

/**
 * The locales this package ships CHROME bundles for — DERIVED from `BUNDLES`, never typed out
 * (ENUM-DRIFT-001). The documents themselves are English only (`LEGAL_DOCUMENT_LANGUAGE`).
 *
 * @constant {ReadonlyArray<string>}
 */
export const SUPPORTED_LOCALES = Object.freeze(Object.keys(BUNDLES));

/**
 * Register one or more legal-page chrome bundles on a react-i18next instance.
 *
 * @param {object} i18nInstance - The react-i18next instance.
 * @param {string|string[]} [locales='en'] - Locale code(s) to load.
 * @returns {object} The same instance, for chaining.
 * @throws {Error} If the instance lacks `addResourceBundle`, or a locale is unsupported.
 */
export function loadLegalContentStrings(i18nInstance, locales = 'en') {
  if (!i18nInstance || typeof i18nInstance.addResourceBundle !== 'function') {
    throw new Error(
      'loadLegalContentStrings: expected a react-i18next instance with addResourceBundle()',
    );
  }
  const list = Array.isArray(locales) ? locales : [locales];
  list.forEach((locale) => {
    const bundle = BUNDLES[locale];
    if (!bundle) {
      throw new Error(
        `loadLegalContentStrings: unsupported locale "${locale}". Supported: ${SUPPORTED_LOCALES.join(', ')}`,
      );
    }
    // Deep merge so an app may add keys; never overwrite, so a caller-defined value wins.
    i18nInstance.addResourceBundle(locale, LEGAL_CONTENT_NAMESPACE, bundle, true, false);
  });
  return i18nInstance;
}

export { resolveString } from './resolveString.js';
